import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { Row, Col, Button } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./ThemeSelector.css";
import Select from "react-select";
import { generate } from "shortid";
import { applyCssVars } from "../useTheme";

require("highcharts/modules/accessibility")(Highcharts);

/** Safe JSON getter */
function getLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * ThemeSelector
 * - Self-hydrates catalogs (all-themes, chart-themes) & active themes from localStorage
 * - Accepts props to override anything if parent provides them
 */
const ThemeSelector = ({
  data: dataProp = {},
  chartData: chartDataProp = {},
  activeTheme: activeThemeProp,
  activeChartTheme: activeChartThemeProp,
  onSelectTheme,
  onSelectChartTheme,
  onThemesChanged,
}) => {
  /** 1) Hydrate catalogs (prefer props, fallback to LS seeded by AppWrapper) */
  const [data, setData] = useState(
    Object.keys(dataProp).length ? dataProp : getLS("all-themes", {})
  );
  const [chartData, setChartData] = useState(
    Object.keys(chartDataProp).length ? chartDataProp : getLS("chart-themes", {})
  );

  /** If parent later passes catalogs, adopt them */
  useEffect(() => {
    if (Object.keys(dataProp).length) setData(dataProp);
  }, [dataProp]);
  useEffect(() => {
    if (Object.keys(chartDataProp).length) setChartData(chartDataProp);
  }, [chartDataProp]);

  /** 2) Hydrate active selections (prefer props, fallback to LS) */
  const [activeTheme, setActiveTheme] = useState(
    activeThemeProp || getLS("theme", null)
  );
  const [activeChartTheme, setActiveChartTheme] = useState(
    activeChartThemeProp || getLS("chart-theme", null)
  );

  useEffect(() => {
    if (activeThemeProp) setActiveTheme(activeThemeProp);
  }, [activeThemeProp]);
  useEffect(() => {
    if (activeChartThemeProp) setActiveChartTheme(activeChartThemeProp);
  }, [activeChartThemeProp]);

  const saveActiveToApi = async (parameter_name, value) => {
    const firm_id = localStorage.getItem("firmId");
    if (!firm_id || !value) return;
    try {
      await fetch(`${process.env.REACT_APP_BASE_URI}/workflow/personalization/set_params/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firm_id,
          parameter_name,
          parameter_value_1: JSON.stringify(value),
        }),
      });
    } catch (err) {
      console.error(`Failed to save ${parameter_name}:`, err);
    }
  };

  useEffect(() => {
    const onCatalog = (e) => {
      // Re-read from LS (source of truth) in case other pages touched it
      const nextThemes = getLS("all-themes", {});
      const nextChart = getLS("chart-themes", {});
      setData(nextThemes);
      setChartData(nextChart);
    };

    const onActive = (e) => {
      // Re-read actives from LS and update
      const nextActive = getLS("theme", null);
      const nextActiveChart = getLS("chart-theme", null);
      if (nextActive) setActiveTheme(nextActive);
      if (nextActiveChart) setActiveChartTheme(nextActiveChart);
    };

    window.addEventListener("cygnus:themes:catalog", onCatalog);
    window.addEventListener("cygnus:themes:active", onActive);
    return () => {
      window.removeEventListener("cygnus:themes:catalog", onCatalog);
      window.removeEventListener("cygnus:themes:active", onActive);
    };
  }, []);


  /** 3) Derive keys & available items */
  const themeKeys = Object.keys(data);
  const chartKeys = Object.keys(chartData);

  const availableThemes = React.useMemo(() => {
    if (!themeKeys.length) return [];
    const activeKey = activeTheme?.name ? _.camelCase(activeTheme.name) : null;
    return activeKey ? themeKeys.filter((k) => k !== activeKey) : themeKeys;
  }, [themeKeys, activeTheme]);

  /** 4) Switchers */
  const themeSwitcher = useCallback(
    (t) => {
      setActiveTheme(t);
      localStorage.setItem("theme", JSON.stringify(t));
      saveActiveToApi("activeTheme", t);
      onSelectTheme?.(t);
      applyCssVars(t); // hot-swap immediately
    },
    [onSelectTheme]
  );

  const chartThemeSwitcher = useCallback(
    (ct) => {
      setActiveChartTheme(ct);
      localStorage.setItem("chart-theme", JSON.stringify(ct));
      saveActiveToApi("activeChartTheme", ct);
      onSelectChartTheme?.(ct);
    },
    [onSelectChartTheme]
  );

  const saveThemesToApi = async (mergedThemes) => {
    const firm_id = localStorage.getItem("firmId");
    if (!firm_id) return;
    try {
      await fetch(`${process.env.REACT_APP_BASE_URI}/workflow/personalization/set_params/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firm_id,
          parameter_name: "themes",
          parameter_value_1: JSON.stringify(mergedThemes),
        }),
      });
    } catch (err) {
      console.error("Failed to save theme to API:", err);
    }
  };

  /** 5) Cards */
  const ThemeCard = ({ theme }) => {
    if (!theme?.colors) return null;
    return (
      <Col onClick={() => themeSwitcher(theme)} className="theme-wrapper" md={2}>
        <div style={{ backgroundColor: theme.colors.primary }} className="theme-layer1">
          <span className="theme-val2" style={{ backgroundColor: theme.colors.quarternary }} />
        </div>
        <div style={{ backgroundColor: theme.colors.secondary }} className="theme-layer2">
          <span className="theme-check" style={{ backgroundColor: theme.colors.quarternary }} />
          <span className="theme-val" style={{ backgroundColor: theme.colors.quarternary }} />
        </div>
        <div style={{ backgroundColor: theme.colors.secondary }} className="theme-layer3">
          <span className="theme-check" style={{ backgroundColor: theme.colors.quarternary }} />
          <span className="theme-val" style={{ backgroundColor: theme.colors.quarternary }} />
        </div>
        <div style={{ backgroundColor: theme.colors.tertiary }} className="theme-layer4">
          <span className="theme-check" style={{ backgroundColor: theme.colors.quarternary }} />
          <span className="theme-val" style={{ backgroundColor: theme.colors.quarternary }} />
        </div>
        <div
          style={{ backgroundColor: theme.colors.secondary, color: theme.colors.body }}
          className="theme-button"
        >
          {theme.name}
        </div>
      </Col>
    );
  };

  const ChartThemeCard = ({ ct }) => {
    const opts = {
      chart: {
        type: "pie",
        innerWidth: 80,
        height: 200,
        width: 200,
        backgroundColor: "transparent",
      },
      colors: ct?.colors,
      title: {
        align: "center",
        text: ct?.name,
        style: { fontSize: "12px", color: "var(--text_color)" },
      },
      plotOptions: { series: { borderWidth: 1, dataLabels: { enabled: false } } },
      series: [{ name: "Theme", colorByPoint: true, data: Array(10).fill({ y: 10 }) }],
      exporting: { enabled: false },
    };
    return (
      <Col onClick={() => chartThemeSwitcher(ct)} className="theme-wrapper" md={2}>
        <div className="chart-container">
          <HighchartsReact highcharts={Highcharts} options={opts} />
        </div>
      </Col>
    );
  };

  /** 6) Create custom theme */
  const defaultTheme = {
    themeName: "",
    priColor: "#350d36",
    secColor: "#3f0e40",
    terColor: "#1164a3",
    quarColor: "#9f869f",
    bgColor: "#FFFFFF",
    txtColor: "#000000",
    font: "Arial",
  };
  const [state, setState] = useState(defaultTheme);
  const [createMsg, setCreateMsg] = useState(false);

  const newThemeObj = React.useMemo(() => {
    if (!state.themeName.trim()) return {};
    const key = _.camelCase(state.themeName);
    return {
      [key]: {
        id: generate(),
        name: state.themeName,
        colors: {
          primary: state.priColor,
          secondary: state.secColor,
          tertiary: state.terColor,
          quarternary: state.quarColor,
          body: state.bgColor,
          text: state.txtColor,
          button: { text: state.bgColor, background: state.priColor },
          link: { text: state.txtColor, opacity: 1 },
        },
        font: state.font,
      },
    };
  }, [state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((s) => ({ ...s, [name]: value }));
  };

  const createTheme = async () => {
    if (!Object.keys(newThemeObj).length) return;

    const merged = { ...(data || {}), ...newThemeObj };

    // 1) update local state + LS
    setData(merged);
    localStorage.setItem("all-themes", JSON.stringify(merged));

    // 2) persist to server so it survives next login
    await saveThemesToApi(merged);

    // 3) notify other components (ThemeSelector on other tabs etc.)
    onThemesChanged?.(merged);
    window.dispatchEvent(new CustomEvent("cygnus:themes:catalog", { detail: { catalog: merged } }));

    setCreateMsg(true);
    setTimeout(() => setCreateMsg(false), 1500);
  };

  /** 7) Chart category filter */
  const [selectedOption, setSelectedOption] = useState("Default");
  const [filteredChartNames, setFilteredChartNames] = useState([]);

  const filterChartThemes = useCallback(
    (value) => {
      if (!Object.keys(chartData).length) {
        setFilteredChartNames([]);
        return;
      }
      const out = [];
      for (const key in chartData) {
        const item = chartData[key];
        // If no category metadata, show all under "Default"
        if (!item?.category) {
          if (value === "Default") out.push(item.name || key);
        } else if (item.category === value) {
          out.push(item.name || key);
        }
      }
      setFilteredChartNames(out);
    },
    [chartData]
  );

  useEffect(() => {
    filterChartThemes(selectedOption);
  }, [chartData, selectedOption, filterChartThemes]);

  const options = [
    { value: "Default", label: "Default" },
    { value: "PowerBI", label: "PowerBI" },
    { value: "Tableau", label: "Tableau" },
  ];

  const activeChartOptions = {
    chart: {
      type: "pie",
      innerWidth: 80,
      height: 200,
      width: 200,
      backgroundColor: "transparent",
    },
    colors: activeChartTheme?.colors,
    title: {
      align: "center",
      text: activeChartTheme?.name,
      style: { fontSize: "12px", color: "var(--text_color)" },
    },
    plotOptions: { series: { borderWidth: 1, dataLabels: { enabled: false } } },
    series: [{ name: "Theme", colorByPoint: true, data: Array(10).fill({ y: 10 }) }],
    exporting: { enabled: false },
  };

  /** 8) Render */
  return (
    <div className="themes-modal-wrapper">
      <Tabs defaultActiveKey="select" id="theme-tabs" className="mb-6">
        {/* UI THEMES */}
        <Tab eventKey="select" title="Themes" className="tabs">
          <Row className="row">
            {activeTheme?.colors && (
              <Col className="theme-wrapper" md={2}>
                <div className="active-theme">
                  <div style={{ backgroundColor: activeTheme.colors.primary }} className="theme-layer1">
                    <span className="theme-val2" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                  </div>
                  <div style={{ backgroundColor: activeTheme.colors.secondary }} className="theme-layer2">
                    <span className="theme-check" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                    <span className="theme-val" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                  </div>
                  <div style={{ backgroundColor: activeTheme.colors.secondary }} className="theme-layer3">
                    <span className="theme-check" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                    <span className="theme-val" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                  </div>
                  <div style={{ backgroundColor: activeTheme.colors.tertiary }} className="theme-layer4">
                    <span className="theme-check" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                    <span className="theme-val" style={{ backgroundColor: activeTheme.colors.quarternary }} />
                  </div>
                  <div
                    style={{ backgroundColor: activeTheme.colors.secondary, color: activeTheme.colors.body }}
                    className="theme-button"
                  >
                    {activeTheme.name}
                  </div>
                </div>
              </Col>
            )}

            {/* Available themes */}
            {availableThemes.length > 0 ? (
              availableThemes.map((key) => <ThemeCard theme={data[key]} key={key} />)
            ) : (
              <Col><div className="empty-hint">No themes available. Create one in the tab above.</div></Col>
            )}
          </Row>
        </Tab>

        {/* CREATE THEME */}
        <Tab eventKey="create" title="Create theme" className="tabs">
          <Row className="row create-theme">
            <Col md="3">
              <Row className="name-inputs"><label>Theme Name</label></Row>
              <Row className="color-inputs"><label>Primary</label></Row>
              <Row className="color-inputs"><label>Secondary</label></Row>
              <Row className="color-inputs"><label>Tertiary</label></Row>
              <Row className="color-inputs"><label>Quarternary</label></Row>
              <Row className="color-inputs"><label>Background</label></Row>
              <Row className="color-inputs"><label>Text</label></Row>
            </Col>

            <Col md="3">
              <Row className="name-inputs">
                <input type="text" name="themeName" value={state.themeName} placeholder="Theme name" onChange={handleChange} />
              </Row>
              {["priColor", "secColor", "terColor", "quarColor", "bgColor", "txtColor"].map((n) => (
                <Row className="color-inputs" key={n}>
                  <input type="color" name={n} value={(state)[n]} onChange={handleChange} />
                </Row>
              ))}
            </Col>

            <Col md="6">
              <Button
                style={{ margin: "1rem 0", padding: "10px 20px", backgroundColor: state.priColor, color: state.bgColor, borderRadius: "10px" }}
                onClick={createTheme}
                disabled={state.themeName.trim().length === 0}
              >
                Create theme
              </Button>
              {createMsg && <div className="create-theme-message"><p>New theme created!</p></div>}
            </Col>
          </Row>
        </Tab>

        {/* CHART THEMES */}
        <Tab eventKey="select chart" title="Chart themes" className="tabs">
          <div className="select-holder">
            <Select
              options={[
                { value: "Default", label: "Default" },
                { value: "PowerBI", label: "PowerBI" },
                { value: "Tableau", label: "Tableau" },
              ]}
              defaultValue={{ value: "Default", label: "Default" }}
              menuPosition="fixed"
              className="cat-selector"
              onChange={(opt) => setSelectedOption(opt?.value || "Default")}
            />
          </div>

          <Row className="row">
            {activeChartTheme && (
              <Col className="theme-wrapper" md={2}>
                <div className="active-chart-theme">
                  <div className="chart-container">
                    <HighchartsReact highcharts={Highcharts} options={activeChartOptions} />
                  </div>
                </div>
              </Col>
            )}

            {filteredChartNames.length ? (
              filteredChartNames.map((name) => (
                <ChartThemeCard ct={chartData[name]} key={name} />
              ))
            ) : (
              <Col><div className="empty-hint">No chart themes in this category.</div></Col>
            )}
          </Row>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ThemeSelector;
